import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon
} from '@mui/icons-material'
import { useGetCigarQuery } from '@store/api/cigarApi'
import CigarForm from '@components/cigars/CigarForm'
import { format } from 'date-fns'

const CigarDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)

  const { data: cigar, isLoading, error, refetch } = useGetCigarQuery(id!, {
    skip: !id
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success'
      case 'smoked': return 'default'
      case 'traded': return 'info'
      case 'gifted': return 'warning'
      case 'damaged': return 'error'
      default: return 'default'
    }
  }

  const formatPrice = (price?: number | string) => {
    if (price == null) return 'N/A'
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    if (isNaN(numPrice)) return 'N/A'
    return `$${numPrice.toFixed(2)}`
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    try {
      return format(new Date(dateStr), 'MMM d, yyyy')
    } catch {
      return 'N/A'
    }
  }

  const handleEditClose = () => {
    setEditOpen(false)
    refetch()
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !cigar) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/collection')} sx={{ mb: 2 }}>
          Back to Collection
        </Button>
        <Alert severity="error">Cigar not found or failed to load.</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/collection')}>
          Back to Collection
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setEditOpen(true)}
          data-testid="edit-cigar-button"
        >
          Edit Cigar
        </Button>
      </Box>

      <Card>
        <CardContent>
          {/* Title */}
          <Box mb={3}>
            <Typography variant="h5" component="h1" gutterBottom>
              {cigar.brand.name} — {cigar.cigarLine.name}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={cigar.status}
                color={getStatusColor(cigar.status) as any}
                size="small"
              />
              {cigar.vitola && (
                <Chip label={cigar.vitola.name} variant="outlined" size="small" />
              )}
              {cigar.strengthLevel && (
                <Chip label={cigar.strengthLevel} variant="outlined" size="small" />
              )}
            </Stack>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Brand</Typography>
              <Typography variant="body1">{cigar.brand.name}</Typography>
              {cigar.brand.country && (
                <Typography variant="body2" color="text.secondary">{cigar.brand.country}</Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Cigar Line</Typography>
              <Typography variant="body1">{cigar.cigarLine.name}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Humidor</Typography>
              <Typography variant="body1">{cigar.humidor.name}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Quantity</Typography>
              <Typography variant="body1">{cigar.quantity}</Typography>
            </Grid>

            {/* Dimensions */}
            {(cigar.length || cigar.ringGauge) && (
              <>
                {cigar.length ? (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Length</Typography>
                    <Typography variant="body1">{cigar.length}&quot;</Typography>
                  </Grid>
                ) : null}
                {cigar.ringGauge ? (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Ring Gauge</Typography>
                    <Typography variant="body1">{cigar.ringGauge}</Typography>
                  </Grid>
                ) : null}
              </>
            )}

            {/* Tobacco */}
            {cigar.wrapperType && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Wrapper</Typography>
                <Typography variant="body1">{cigar.wrapperType}</Typography>
              </Grid>
            )}
            {cigar.binderType && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Binder</Typography>
                <Typography variant="body1">{cigar.binderType}</Typography>
              </Grid>
            )}
            {cigar.fillerType && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Filler</Typography>
                <Typography variant="body1">{cigar.fillerType}</Typography>
              </Grid>
            )}

            {/* Purchase Info */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="medium" mb={2}>Purchase Information</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Price</Typography>
              <Typography variant="body1">{formatPrice(cigar.purchasePrice)}</Typography>
            </Grid>

            {cigar.purchaseDate && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Purchase Date</Typography>
                <Typography variant="body1">{formatDate(cigar.purchaseDate)}</Typography>
              </Grid>
            )}

            {cigar.purchaseLocation && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Purchase Location</Typography>
                <Typography variant="body1">{cigar.purchaseLocation}</Typography>
              </Grid>
            )}

            {cigar.batchNumber && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Batch Number</Typography>
                <Typography variant="body1">{cigar.batchNumber}</Typography>
              </Grid>
            )}

            {/* Notes */}
            {cigar.notes && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
                <Typography variant="body1">{cigar.notes}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <CigarForm
        open={editOpen}
        onClose={handleEditClose}
        cigar={cigar}
      />
    </Box>
  )
}

export default CigarDetail
